/**
 * Hook för Heatmap-vyn
 * Hanterar state och logik för heatmap-visualisering
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from '@/store/languageStore';
import { vehiclesService, Vehicle } from '@/services/vehicles/vehiclesService';
import { heatmapService, HeatmapPoint } from '@/services/heatmap/heatmapService';
import { heatmapImportService } from '@/services/heatmapImport/heatmapImportService';
import toast from 'react-hot-toast';
import {
  HeatmapMode,
  TimePattern,
  getDefaultDateRange,
} from '@/types/heatmap';

interface UseHeatmapProps {
  sessionId?: string;
  isImport?: boolean;
}

export function useHeatmap({ sessionId, isImport = false }: UseHeatmapProps) {
  const { t } = useTranslation();
  const defaultDates = getDefaultDateRange();

  // Data state
  const [allVehicles, setAllVehicles] = useState<Vehicle[]>([]);
  const [importVehicles, setImportVehicles] = useState<string[]>([]);
  const [points, setPoints] = useState<HeatmapPoint[]>([]);
  const [tripCount, setTripCount] = useState(0);

  // Filter state
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [mode, setMode] = useState<HeatmapMode>('all');
  const [timePattern, setTimePattern] = useState<TimePattern>('all');
  const [fromDate, setFromDate] = useState<string>(defaultDates.fromDate);
  const [toDate, setToDate] = useState<string>(defaultDates.toDate);

  // UI state
  const [loading, setLoading] = useState(false);
  const [loadingVehicles, setLoadingVehicles] = useState(false);
  const [showInfoModal, setShowInfoModal] = useState(false);

  // Computed vehicles list
  const vehicles = useMemo<Vehicle[]>(() => {
    if (isImport) {
      return importVehicles.map(
        (reg) =>
          ({
            id: reg,
            registration_number: reg,
            make: '',
            model: '',
          } as Vehicle)
      );
    }
    return allVehicles;
  }, [allVehicles, importVehicles, isImport]);

  // Load vehicles
  useEffect(() => {
    const loadVehicles = async () => {
      if (isImport && sessionId) {
        try {
          setLoadingVehicles(true);
          const vehicleList = await heatmapImportService.getVehicles(sessionId);
          setImportVehicles(vehicleList);
        } catch {
          toast.error(
            t('heatmap.errorLoadingVehicles') || 'Kunde inte ladda fordon'
          );
        } finally {
          setLoadingVehicles(false);
        }
      } else {
        try {
          setLoadingVehicles(true);
          const params = selectedOrganizationId
            ? {
                organizationId: selectedOrganizationId,
                limit: 10000,
                includeSubOrgs: true,
              }
            : { limit: 10000, includeSubOrgs: true };
          const result = await vehiclesService.getVehicles(params);
          setAllVehicles(result.vehicles);
        } catch {
          toast.error(
            t('heatmap.errorLoadingVehicles') || 'Kunde inte ladda fordon'
          );
        } finally {
          setLoadingVehicles(false);
        }
      }
    };
    loadVehicles();
  }, [t, selectedOrganizationId, isImport, sessionId]);

  // Clear vehicle selection when organization changes
  useEffect(() => {
    setSelectedVehicleId('');
  }, [selectedOrganizationId]);

  // Load heatmap data
  const loadHeatmapData = useCallback(async () => {
    try {
      setLoading(true);
      setPoints([]);

      let vehicleIds: string;
      if (selectedVehicleId) {
        vehicleIds = selectedVehicleId;
      } else if (isImport) {
        vehicleIds = 'all';
      } else if (selectedOrganizationId) {
        const orgVehicleIds = vehicles.map((v) => String(v.id)).join(',');
        vehicleIds = orgVehicleIds || 'all';
      } else {
        vehicleIds = 'all';
      }

      const loadingToast = toast.loading(
        t('heatmap.loadingData') || 'Laddar heatmap-data...'
      );

      const startTime = Date.now();
      let data;

      if (isImport && sessionId) {
        data = await heatmapImportService.getPoints(sessionId, {
          vehicleIds,
          from: `${fromDate}T00:00:00.000Z`,
          to: `${toDate}T23:59:59.999Z`,
          mode,
        });
      } else {
        data = await heatmapService.getHeatmapPoints({
          vehicleIds,
          from: `${fromDate}T00:00:00.000Z`,
          to: `${toDate}T23:59:59.999Z`,
          mode,
          timePattern: mode === 'timepattern' ? timePattern : undefined,
        });
      }

      const loadTime = ((Date.now() - startTime) / 1000).toFixed(1);

      setPoints(data.points);
      setTripCount(data.tripCount);

      toast.dismiss(loadingToast);
      toast.success(
        t('heatmap.dataLoaded')
          ?.replace('{count}', String(data.points.length))
          ?.replace('{time}', loadTime) ||
          `${data.points.length} punkter laddade på ${loadTime}s`
      );
    } catch {
      toast.error(
        t('heatmap.errorLoadingData') || 'Kunde inte ladda heatmap-data'
      );
    } finally {
      setLoading(false);
    }
  }, [
    selectedVehicleId,
    selectedOrganizationId,
    vehicles,
    isImport,
    sessionId,
    fromDate,
    toDate,
    mode,
    timePattern,
    t,
  ]);

  // Handlers
  const handleVehicleChange = useCallback((vehicleId: string) => {
    setSelectedVehicleId(vehicleId);
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedVehicleId('');
  }, []);

  const handleOrganizationChange = useCallback((orgId: string) => {
    setSelectedOrganizationId(orgId);
  }, []);

  const handleModeChange = useCallback((newMode: HeatmapMode) => {
    setMode(newMode);
  }, []);

  const handleTimePatternChange = useCallback((newPattern: TimePattern) => {
    setTimePattern(newPattern);
  }, []);

  const openInfoModal = useCallback(() => {
    setShowInfoModal(true);
  }, []);

  const closeInfoModal = useCallback(() => {
    setShowInfoModal(false);
  }, []);

  return {
    // Data
    vehicles,
    points,
    tripCount,

    // Filter state
    selectedOrganizationId,
    selectedVehicleId,
    mode,
    timePattern,
    fromDate,
    toDate,

    // UI state
    loading,
    loadingVehicles,
    showInfoModal,

    // Setters
    setFromDate,
    setToDate,

    // Actions
    loadHeatmapData,
    handleVehicleChange,
    handleSelectAll,
    handleOrganizationChange,
    handleModeChange,
    handleTimePatternChange,
    openInfoModal,
    closeInfoModal,
  };
}
